import { useMemo, useState } from 'react';
import Stripe from 'stripe';

import { BOOKING_STATUS_COLORS } from 'lib/constants/bookings';
import { contactHelp } from 'lib/db';
import { buildGMapsLink } from 'lib/geo';
import { getListingGalleryImages } from 'lib/images';
import { formatCurrency } from 'lib/utils/currency';
import { firstNameProcessor } from 'lib/utils/string';
import {
  BookingDoc,
  BookingGuests,
  BookingStatus,
  ListingDoc,
  PrivateListingDoc,
  ProfileDoc,
  Timestamp,
  WithId,
} from 'schemas/types';

import { prompts } from 'components/alert';
import Button from 'components/form/Button';
import Link from 'components/Link';
import { PhotoGallery } from 'components/Listing/PhotoGallery';
import Modal from 'components/Modal';
import { CardPreview } from 'components/payments/CustomerPaymentMethod';
import { ProfileCard } from 'components/social/ProfileCard';
import { FormatDate } from 'components/Time';

export const BookingPageTitle = ({
  booking: {
    status,
    canceled_by,
    lister: { id: hostId },
    listing,
  },
  userId,
}: {
  booking: WithId<BookingDoc>;
  userId: string;
}) => {
  const isHost = userId === hostId;

  const statusText = <span className={`${BOOKING_STATUS_COLORS[status]}`}>{status}</span>;

  if (status === 'canceled')
    return (
      <>
        {userId === canceled_by ? 'You' : `The ${isHost ? 'guest' : 'host'}`} {statusText} the
        reservation
      </>
    );

  return (
    <>
      {isHost ? 'The' : 'Your'} reservation request in{' '}
      <span className="tw-underline">{listing.title}</span>{' '}
      {status === 'expired' ? 'has' : status === 'denied' ? 'was' : 'is'} {statusText}
      {status === 'confirmed' && <> 🎉</>}
    </>
  );
};

export const BookingDates = ({ startAt, endAt }: { startAt: Timestamp; endAt: Timestamp }) => {
  return (
    <>
      <div className="dates" style={{ width: 264, marginBottom: 10 }}>
        <div className="date">
          <div className="date-box">
            <span className="month">
              <FormatDate time={startAt} format="MMM" />{' '}
            </span>
            <span className="day">
              <FormatDate time={startAt} format="dd" />
            </span>
          </div>
          <div className="date-info flex items-center has-text-centered">
            <span className="is-size-7">
              <FormatDate time={startAt} format="EEEE" /> <b>check-in</b> after{' '}
            </span>
          </div>
          <div className="date-box" style={{ marginLeft: '10px' }}>
            <span className="month">
              <FormatDate time={startAt} format="p" />
            </span>
          </div>
        </div>
      </div>
      <div className="dates" style={{ width: 264, marginBottom: 20 }}>
        <div className="date">
          <div className="date-box">
            <span className="month">
              <FormatDate time={endAt} format="MMM" />{' '}
            </span>
            <span className="day">
              <FormatDate time={endAt} format="dd" />
            </span>
          </div>
          <div className="date-info flex items-center has-text-centered">
            <span className="is-size-7">
              <FormatDate time={endAt} format="EEEE" /> <b>check-out</b> before{' '}
            </span>
          </div>
          <div className="date-box" style={{ marginLeft: '10px' }}>
            <span className="month">
              <FormatDate time={endAt} format="p" />
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export const BookingDatesMinimal = ({
  startAt,
  endAt,
}: {
  startAt: Timestamp;
  endAt: Timestamp;
}) => {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
        <div className="tw-text-sm tw-font-secondary tw-text-ebony">
          <FormatDate time={startAt} format="MMM, do" />
        </div>
        <i
          className="fa fa-arrow-right has-text-grey"
          style={{ marginLeft: '5px', marginRight: '5px' }}
          aria-label="through"
        />
        <div className="date">
          <div className="tw-text-sm tw-font-secondary tw-text-ebony">
            <FormatDate time={endAt} format="MMM, do" />
          </div>
        </div>
      </div>
    </>
  );
};

export const BookingHeroContent = ({
  listing,
  guests,
  privateListing,
}: {
  listing: Omit<WithId<ListingDoc>, 'unavailableDays'>;
  guests: BookingGuests;
  privateListing?: WithId<PrivateListingDoc>;
}) => {
  const items = useMemo(() => (listing ? getListingGalleryImages(listing) : null), [listing]);

  return (
    <div className="tw-flex tw-flex-col md:tw-flex-row">
      <div className="tw-w-full md:tw-w-[40%] tw-h-[250px] tw-mb-8 md:tw-mb-0">
        <PhotoGallery items={items} className="tw-h-full tw-w-full" />
      </div>
      <div className="tw-flex-1 md:tw-px-8 tw-flex tw-flex-col tw-justify-center">
        <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-0 md:tw-mb-10">
          <p className="tw-text-brown tw-font-secondary tw-font-bold md:tw-text-lg">
            Who&apos;s coming
          </p>
          <div className="tw-grid tw-grid-cols-2 md:tw-flex tw-items-center tw-gap-16 tw-text-ebony tw-font-secondary">
            <div>
              <p className="tw-font-semibold">Adults</p>
              <p className="tw-font-sans-serif">{guests.adults}</p>
            </div>
            {guests.children ? (
              <div>
                <p className="tw-font-semibold">Children</p>
                <p className="tw-font-sans-serif">{guests.children}</p>
              </div>
            ) : undefined}
            {listing.rules.includes('IN') && guests.infants ? (
              <div>
                <p className="tw-font-semibold">Infants</p>
                <p className="tw-font-sans-serif">{guests.infants}</p>
              </div>
            ) : undefined}
            {listing.rules.includes('PE') && guests.pets ? (
              <div>
                <p className="tw-font-semibold">Pets</p>
                <p className="tw-font-sans-serif">{guests.pets}</p>
              </div>
            ) : undefined}
          </div>
        </div>
        <p className="tw-text-ebony tw-font-secondary">
          {listing.city}, {listing.state} {listing.zip_code}, {listing.country}
        </p>

        {privateListing && privateListing.address && (
          <div>
            <Link href={buildGMapsLink(privateListing.geocoords, privateListing.address)} newTab>
              <Button variant="outlinedAlt" className="tw-text-sm">
                <i className="fa-regular fa-map tw-mr-3" />
                Get Directions
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export const BookingOtherUserContent = ({
  profile,
  bookingId,
  bookingStatus,
  phone,
  isHost = false,
}: {
  profile: WithId<ProfileDoc>;
  bookingId: string;
  bookingStatus: BookingStatus;
  phone?: string | null;
  isHost?: boolean;
}) => {
  return (
    <>
      <p className="tw-text-brown tw-font-secondary tw-font-bold tw-text-lg">
        Your {isHost ? 'guest' : 'host'} is {profile.firstName}
      </p>
      <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between">
        <ProfileCard profile={profile} className="tw-mb-4 md:tw-mb-0" />
        <div className="tw-flex tw-flex-row tw-gap-5">
          <Link href={`/chats/${bookingId}`} newTab>
            <Button variant="outlinedAlt" className="tw-text-nowrap">
              <i className="fa-regular fa-comment-dots tw-mr-3" />
              Message {firstNameProcessor(profile.firstName, 8, isHost ? 'Guest' : 'Host')}
            </Button>
          </Link>
          {bookingStatus === 'confirmed' && phone ? (
            <Link href={`tel:${phone}`}>
              <Button variant="outlinedAlt" className="tw-text-nowrap">
                <i className="fa-regular fa-phone tw-mr-3" /> {phone}
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </>
  );
};

export const BookingPaymentStatus = ({
  booking,
  paymentMethod,
}: {
  booking: WithId<BookingDoc>;
  paymentMethod: Stripe.PaymentMethod;
}) => {
  return (
    <>
      <p className="tw-text-brown tw-font-secondary tw-font-bold tw-text-lg">Payment Status</p>
      <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-justify-between">
        <div className="tw-mb-4 md:tw-mb-0">
          <p className="tw-text-ebony tw-font-secondary">
            Total of{' '}
            <span className="tw-font-bold">
              {booking.guest_payment_amount && formatCurrency(booking.guest_payment_amount)}
            </span>
          </p>
        </div>
        <div>
          <CardPreview card={paymentMethod} />
        </div>
      </div>
    </>
  );
};

export const BookingHelpModal = ({
  isHost,
  bookingId,
  active,
  onClose,
}: {
  isHost: boolean;
  bookingId: string;
  active: boolean;
  onClose: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  return (
    <Modal<true>
      active={active}
      onClose={onClose}
      loading={loading}
      header="Need Help?"
      labels={{ confirm: 'Request help', cancel: 'Nevermind' }}
      isForm
      onConfirm={async (values) => {
        setLoading(true);
        await contactHelp({
          message: values?.message,
          booking_id: bookingId,
        });
        setLoading(false);
        onClose();

        prompts.alert('Message sent! You should here back from us shortly.');
      }}
    >
      {isHost ? (
        <p>
          Need to cancel after a booking starts? Want to refund your guest? Any other questions? Ask
          us here:
        </p>
      ) : (
        <p>
          Need to cancel after a booking starts? Need to request a refund? Any other questions? Ask
          us here:
        </p>
      )}
      <br />
      <div className="field">
        <textarea name="message" className="textarea" />
      </div>
    </Modal>
  );
};
